/**
 * «مسدود کردن» در وب — قرینه‌ی `apps/mobile/src/features/blocks/BlockFlow.tsx`.
 *
 * WHOSUP-230، مایگریشن 0146؛ یک‌قدمی شد در WHOSUP-262 (مایگریشن 0154).
 *
 * ═══ چرا فقط یک قدم ═══
 *
 * تا WHOSUP-262 قدمِ دومی بود: بعد از بلاک، فهرستِ گروه‌های مشترک و پرسشِ
 * «در این‌ها هم از هم جدا شوید؟». حذف شد چون گروه در این اپ شخصی است — مالِ
 * سازنده‌اش — و آن فهرست نامِ گروه‌های طرفِ مقابل را به بلاک‌کننده نشان
 * می‌داد. حالا خودِ `block_user` بی‌صدا هر کدام را از گروه‌های دیگری
 * برمی‌دارد و چیزی برنمی‌گرداند؛ این‌جا فقط تأیید است.
 *
 * ═══ چرا مودال و نه منوی کشویی ═══
 *
 * همان سه دلیلی که بالای `features/report/ReportMenu.tsx` نوشته شده
 * (عرضِ ظرف، stacking context، ارتفاعِ ردیف). این دو دکمه کنارِ هم می‌نشینند
 * و باید هم‌شکل رفتار کنند.
 */
import React, { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { blockRepo } from '@/core/data/blockRepo';
import { useSession } from '@/core/session';
import { t } from '@/core/lib/i18n';
import { reportError } from '@/core/lib/report';
import { BLOCK_STALE_QUERIES } from '@/core/lib/block';
import { Card, Note } from '@/ui/kit';
import { CancelledIcon } from '@/ui/icons';

/* ─────────────────────────── خودِ جریان ─────────────────────────── */

function BlockForm({ person, onClose }: {
  person: { id: string; name: string };
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const block = useMutation({
    mutationFn: () => blockRepo.block(person.id),
    onSuccess: () => {
      // فهرستِ دوستان، گروه‌ها، پیشنهادها و مسدودها دیگر حقیقت نمی‌گویند.
      for (const key of BLOCK_STALE_QUERIES) qc.invalidateQueries({ queryKey: [...key] });
      onClose();
    },
    onError: (e) => { reportError('web.block.block', e); setError(t('errorRetry')); },
  });

  return (
    <>
      <h3>{t('blockTitle', { name: person.name })}</h3>
      <p>{t('blockWhat')}</p>
      <p className="dim">{t('blockKeepsShared')}</p>
      {/* اینکه به او گفته نمی‌شود، خودش بخشی از تصمیم است — نه یک تبصره. */}
      <Note>{t('blockNeverTold')}</Note>

      {error ? <p className="error">{error}</p> : null}

      <div className="chiprow">
        <button type="button" className="btn fill" data-testid="block-confirm"
          disabled={block.isPending} onClick={() => block.mutate()}>
          {block.isPending ? '…' : t('blockConfirm')}
        </button>
        <button type="button" className="btn" onClick={onClose}>
          {t('contactsPrivacyCancel')}
        </button>
      </div>
    </>
  );
}

/* ─────────────────────────── ظرف ─────────────────────────── */

export function BlockDialog({ person, onClose }: {
  person: { id: string; name: string };
  onClose: () => void;
}) {
  // Escape می‌بندد — روی وب کاربر انتظارش را دارد. کلیکِ بیرون هم، پایین.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <Card className="modal" onClick={(e) => e.stopPropagation()}>
        <BlockForm person={person} onClose={onClose} />
      </Card>
    </div>
  );
}

/* ─────────────────────────── ماشه ─────────────────────────── */

/**
 * دکمه‌ی آیکونی، هم‌شکلِ پرچمِ «گزارش» که کنارش می‌نشیند.
 *
 * مهمان دکمه نمی‌گیرد: `block_user` نامِ بلاک‌کننده را از `auth.uid()`
 * می‌خواند، پس دکمه‌ای که کار نمی‌کند بهتر است نباشد — همان قاعده‌ی
 * `ReportButton`.
 */
export function BlockButton({ person }: { person: { id: string; name: string } }) {
  const { profile } = useSession();
  const [open, setOpen] = useState(false);
  const text = t('blockUser');

  if (!profile || profile.id === person.id) return null;

  return (
    <>
      <button type="button" className="iconbtn2 danger" aria-label={text} title={text}
        data-testid="block-open" onClick={() => setOpen(true)}>
        <CancelledIcon size={16} />
      </button>
      {open ? <BlockDialog person={person} onClose={() => setOpen(false)} /> : null}
    </>
  );
}
